import { validatePaidBreaks as validateShiftPaidBreaks } from "./paidBreakValidation";
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import SaveIcon from "@mui/icons-material/Save";
import LoadingButton from "@mui/lab/LoadingButton";
import ShiftCardModalDisplay from "./ShiftCardModalDisplay";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { ThemeProvider } from "@mui/material/styles";
import { createRightworkTheme } from "../../../../theme/rightworkTheme";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InputIcon from "@mui/icons-material/Input";
import CheckIcon from "@mui/icons-material/Check";
import Divider from "@mui/material/Divider";
import {
  getRoleType,
  getRoleName,
  text_to_num,
  checkIfValid,
  get_t_str,
  get_t_str_shortened,
  createDayArray,
} from "../../../../helpers/helpers";
import ShiftSingleInputModal from "./ShiftSingleInputModal";
import DrawerFooterContainerSmall from "../../requests/shared/helpers/DrawerFooterContainerSmall";
import { getScheduleRoleIds } from "./departmentFilter";

// Modal style object
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  maxHeight: 800,
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
};

const theme = createRightworkTheme({
  status: {
    danger: "#e53e3e",
  },
  palette: {
    neutral: {
      main: "#64748B",
      contrastText: "#fff",
    },
  },
});

const PAID_BREAK_DURATION_HOURS = 10 / 60;
const formatShiftBreakSummary = (shift) => {
  const scheduledBreaks = [
    {
      break_start: shift.break_start,
      break_length: shift.break_length,
    },
    {
      break_start: shift.break_start_2,
      break_length: shift.break_length_2,
    },
  ].filter((breakEntry) => (breakEntry.break_length || 0) > 0);

  if (!scheduledBreaks.length) {
    return "No Break";
  }

  return scheduledBreaks
    .map(
      (breakEntry) =>
        `${get_t_str(breakEntry.break_start)} - ${get_t_str(
          breakEntry.break_start + breakEntry.break_length
        )}`
    )
    .join(", ");
};

const MISSED_SHIFT_REASON_LABELS = {
  called_off_sick: "Called out sick",
  called_off_other: "Called out other",
  no_show: "No-call no-show",
  manager_cancelled: "Manager cancelled",
};

const MISSED_SHIFT_REASON_OPTIONS = [
  { value: "called_off_sick", label: "Mark called out sick" },
  { value: "called_off_other", label: "Mark called out other" },
  { value: "no_show", label: "Mark no-call no-show" },
  { value: "manager_cancelled", label: "Mark manager cancelled" },
];

const getDateOnlyString = (dateValue) => {
  if (!dateValue) {
    return null;
  }
  if (typeof dateValue === "string") {
    return dateValue.slice(0, 10);
  }
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getStoreNow = (storeTimezone) => {
  if (!storeTimezone) {
    return new Date();
  }

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: storeTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value;

    return new Date(
      Number(get("year")),
      Number(get("month")) - 1,
      Number(get("day")),
      Number(get("hour")),
      Number(get("minute")),
      Number(get("second"))
    );
  } catch {
    return new Date();
  }
};

const getCurrentBusinessTimeInfo = (storeTimezone, startQhnum) => {
  const storeNow = getStoreNow(storeTimezone);
  const clockQhnum =
    storeNow.getHours() +
    storeNow.getMinutes() / 60 +
    storeNow.getSeconds() / 3600;
  const numericStartQhnum = Number(startQhnum);
  const businessDate = new Date(storeNow);
  const beforeBusinessDayStart =
    Number.isFinite(numericStartQhnum) && clockQhnum < numericStartQhnum;

  if (beforeBusinessDayStart) {
    businessDate.setDate(businessDate.getDate() - 1);
  }

  return {
    businessDate: getDateOnlyString(businessDate),
    businessQhnum: beforeBusinessDayStart ? clockQhnum + 24 : clockQhnum,
  };
};

const hasShiftStartedInStoreTime = (shift, storeTimezone, startQhnum) => {
  const shiftDate = getDateOnlyString(shift?.date);
  const shiftStart = Number(shift?.shift_start);

  if (!shiftDate || !Number.isFinite(shiftStart)) {
    return false;
  }

  const currentBusinessTimeInfo = getCurrentBusinessTimeInfo(
    storeTimezone,
    startQhnum
  );

  if (shiftDate < currentBusinessTimeInfo.businessDate) {
    return true;
  }
  if (shiftDate > currentBusinessTimeInfo.businessDate) {
    return false;
  }

  return shiftStart <= currentBusinessTimeInfo.businessQhnum;
};

function ShiftInputsModal(props) {
  const readOnly = Boolean(props.readOnly);
  const normalizePaidBreaks = React.useCallback(
    (paidBreaks = []) => {
      if (!props.paidBreakInputsEnabled) {
        return [];
      }

      return paidBreaks
        ?.map((paidBreak) => ({
          ...paidBreak,
          break_duration:
            paidBreak?.break_duration || PAID_BREAK_DURATION_HOURS,
        }))
        ?.sort((a, b) => a.break_start - b.break_start) || [];
    },
    [props.paidBreakInputsEnabled]
  );

  const shiftKeyCounter = React.useRef(0);

  const normalizeShift = React.useCallback(
    (shift) => ({
      ...shift,
      _key: shift._key ?? shiftKeyCounter.current++,
      paid_breaks: normalizePaidBreaks(shift?.paid_breaks),
    }),
    [normalizePaidBreaks]
  );

  const [shiftsInfo, setShiftsInfo] = React.useState(() =>
    props.shifts_info?.map((shift) => normalizeShift(shift)) || []
  );

  // For adding/editing shifts from monthly view (with or without pre-selected employee)
  const [localSelectedEmployeeId, setLocalSelectedEmployeeId] = React.useState(
    props.selectedEmployeeId || props.p?.id || (props.store_team_member_weekly_info?.[0]?.id) || null
  );

  // Update local employee selection when prop changes
  React.useEffect(() => {
    if (props.selectedEmployeeId !== undefined) {
      setLocalSelectedEmployeeId(props.selectedEmployeeId);
    } else if (props.p?.id && localSelectedEmployeeId !== props.p.id) {
      // If props.p is provided (from monthly view edit), initialize with that person's ID
      setLocalSelectedEmployeeId(props.p.id);
    }
  }, [props.selectedEmployeeId, props.p?.id]);

  // Get the selected person object
  // For monthly view: allow changing employee via dropdown even if props.p is initially set
  // For weekly view: always use props.p (no dropdown shown)
  const selectedPerson = props.isMonthlyView
    ? props.store_team_member_weekly_info?.find(emp => emp.id === localSelectedEmployeeId) || props.p
    : props.p;
  const clickedShiftId = props.shift_info?.id;
  const primaryShift =
    shiftsInfo?.find((shift) => shift.id === clickedShiftId) || shiftsInfo?.[0];
  const getAttendanceStatusInfo = (shift) => {
    const publishedSchedule = shift?.publishedschedule;
    const missedShiftReason = publishedSchedule?.missed_shift_reason || null;
    const missedShiftLabel =
      MISSED_SHIFT_REASON_LABELS[missedShiftReason] || null;
    const hasActualUsedSchedule =
      publishedSchedule?.has_actual_used_schedule === true;
    const canManageAttendanceForDate =
      props.day_in_past ||
      hasShiftStartedInStoreTime(shift, props.storeTimezone, props.start_qhnum);
    const needsAttendanceReview =
      canManageAttendanceForDate &&
      publishedSchedule &&
      publishedSchedule.has_actual_used_schedule === false &&
      !publishedSchedule.clock_in_optional &&
      !missedShiftReason;
    const attendanceStatusText = missedShiftReason
      ? missedShiftLabel
      : needsAttendanceReview
      ? "No clock entry matched this published shift."
      : hasActualUsedSchedule
      ? "Clock entry matched this published shift."
      : null;
    const canManageAttendanceBase =
      props.existingShift &&
      !props.new_shift &&
      shift?.id > 0 &&
      publishedSchedule &&
      (!hasActualUsedSchedule || missedShiftReason) &&
      typeof props.post_missed_shift === "function";
    const canSetAttendanceStatus =
      canManageAttendanceBase &&
      canManageAttendanceForDate &&
      !hasActualUsedSchedule;
    const canClearAttendanceStatus =
      canManageAttendanceBase &&
      Boolean(missedShiftReason);

    return {
      shift,
      missedShiftReason,
      hasActualUsedSchedule,
      needsAttendanceReview,
      attendanceStatusText,
      showAttendanceStatus: Boolean(attendanceStatusText),
      canSetAttendanceStatus,
      canClearAttendanceStatus,
      canManageAttendanceStatus:
        canSetAttendanceStatus || canClearAttendanceStatus,
    };
  };
  const primaryAttendanceInfo = getAttendanceStatusInfo(primaryShift);
  const hasAnyAttendanceStatusActions = shiftsInfo.some(
    (shift) => getAttendanceStatusInfo(shift).canManageAttendanceStatus
  );
  const canUnassignToOpenShift =
    !readOnly &&
    props.existingShift &&
    !props.new_shift &&
    selectedPerson &&
    shiftsInfo.length === 1 &&
    shiftsInfo[0]?.id > 0 &&
    shiftsInfo[0]?.status !== "AVAILABLE";
  const drawerTitle = readOnly
    ? "Shift Details"
    : props.isMonthlyView
    ? props.new_shift
      ? "Add Shift"
      : "Edit Shift"
    : selectedPerson
    ? `${selectedPerson.first_name} ${selectedPerson.last_name}`
    : "Add Shift";
  const [shiftActionsAnchorEl, setShiftActionsAnchorEl] = React.useState(null);
  const [attendanceActionShiftId, setAttendanceActionShiftId] =
    React.useState(null);
  const [unassignConfirmOpen, setUnassignConfirmOpen] = React.useState(false);
  const [attendanceStatusLoading, setAttendanceStatusLoading] = React.useState(false);
  const attendanceActionShift =
    attendanceActionShiftId !== null
      ? shiftsInfo.find((shift) => shift.id === attendanceActionShiftId) ||
        primaryShift
      : primaryShift;
  const attendanceActionInfo =
    getAttendanceStatusInfo(attendanceActionShift);
  const canSetAttendanceStatus =
    attendanceActionInfo.canSetAttendanceStatus;
  const canClearAttendanceStatus =
    attendanceActionInfo.canClearAttendanceStatus;
  const hasActualUsedSchedule =
    attendanceActionInfo.hasActualUsedSchedule;
  const shiftActionsOpen = Boolean(shiftActionsAnchorEl);
  const showDrawerAttendanceStatus =
    shiftsInfo.length === 1 && primaryAttendanceInfo.showAttendanceStatus;
  const showShiftActions =
    canUnassignToOpenShift || hasAnyAttendanceStatusActions;
  const showAttendanceStatusActions =
    showDrawerAttendanceStatus &&
    primaryAttendanceInfo.canManageAttendanceStatus;
  const showHeaderShiftActions =
    (canUnassignToOpenShift ||
      (shiftsInfo.length === 1 &&
        primaryAttendanceInfo.canManageAttendanceStatus)) &&
    !showAttendanceStatusActions;

  // For monthly view, don't show availability info because store_team_member_weekly_info
  // only contains data for one specific week, but monthly view shows multiple weeks.
  // Showing availability from the wrong week would be misleading (temporary availability
  // or time-off changes won't line up with the selected shift's date).
  // For weekly view, use the date-specific availability_info prop.
  const availabilityInfo = props.isMonthlyView
    ? null
    : props.availability_info;
  const availabilityDotClass =
    availabilityInfo?.available_from === 0 && availabilityInfo?.available_to === 24
      ? "bg-emerald-500"
      : availabilityInfo?.available_to === 0
        ? "bg-rose-500"
        : "bg-amber-400";

  const handleEmployeeChange = (event) => {
    const newEmployeeId = event.target.value;
    setLocalSelectedEmployeeId(newEmployeeId);
    if (props.setSelectedEmployeeId) {
      props.setSelectedEmployeeId(newEmployeeId);
    }
  };

  function getDayIndex(dateString) {
    const daysOfWeek = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const date = new Date(dateString);
    const dayName = daysOfWeek[date.getDay()];
    return daysOfWeek.indexOf(dayName);
  }

  const personFrequentShiftsForWeek =
    props.frequentShifts?.[`${shiftsInfo?.[0]?.store_team_member}`];
  const dowIndex = getDayIndex(shiftsInfo?.[0].date);
  const allowedScheduleRoleIds = new Set(
    getScheduleRoleIds(selectedPerson, props.role_names)
  );
  const roleIsAvailable = (roleId) => allowedScheduleRoleIds.has(roleId);
  const mostFrequentShifts = personFrequentShiftsForWeek?.[dowIndex]?.filter(
    (shift) => roleIsAvailable(shift.role_id_id)
  );

  const fillShiftInputWithFrequentShift = (index) => {
    const frequentShift = mostFrequentShifts?.[index];
    if (!frequentShift) return;

    // Create a copy of the shiftsInfo array
    const updatedShiftsInfo = [...shiftsInfo];

    // Update the object at index 0 with the mostFrequentShifts data
    updatedShiftsInfo[0] = {
      ...updatedShiftsInfo[0],
      shift_start: frequentShift.shift_start,
      shift_end: frequentShift.shift_end,
      shift_length: frequentShift.shift_length,
      role_id: frequentShift.role_id_id,
      role_type: props.role_names?.find(
        (role) => role.id === frequentShift.role_id_id
      )?.role_type,
      break_start: frequentShift.break_start,
      break_length: frequentShift.break_length,
      break_start_2: frequentShift.break_start_2 || 0,
      break_length_2: frequentShift.break_length_2 || 0,
    };

    // Update the shiftsInfo state with the modified array
    setShiftsInfo(updatedShiftsInfo);
  };

  const [savingLoading, setSavingLoading] = React.useState(false);
  const [deletingLoading, setDeletingLoading] = React.useState(false);
  const [deleteShifts, setDeleteShifts] = React.useState([]);


  // Handles the frequent shifts dropdown
  const [isFrequentShiftsDropdownOpen, setIsFrequentShiftsDropdownOpen] =
    React.useState(false);

  const dropdownRefFrequentShifts = React.useRef(null);

  const handleButtonClickFrequentShifts = () => {
    setIsFrequentShiftsDropdownOpen(!isFrequentShiftsDropdownOpen);
  };

  const handleClickOutsideFrequentShifts = (event) => {
    if (
      dropdownRefFrequentShifts.current &&
      !dropdownRefFrequentShifts.current.contains(event.target)
    ) {
      setIsFrequentShiftsDropdownOpen(false);
    }
  };

  const [selectedFrequentShift, setSelectedFrequentShift] = React.useState(0);

  const handleSelectFrequentShift = (shift) => {
    setSelectedFrequentShift(shift);
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideFrequentShifts);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutsideFrequentShifts
      );
    };
  }, []);

  function convertToDayOfWeek(dateString) {
    // Map the first three letters of the day string to the corresponding day of the week
    const dayMappings = {
      Sun: "Sundays",
      Mon: "Mondays",
      Tue: "Tuesdays",
      Wed: "Wednesdays",
      Thu: "Thursdays",
      Fri: "Fridays",
      Sat: "Saturdays",
    };

    // Extract the first three letters of the day string
    const dayAbbreviation = dateString.substring(0, 3);

    // Return the corresponding day of the week in plural form
    return dayMappings[dayAbbreviation];
  }

  const [appliedTo, setAppliedTo] = React.useState([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);

  React.useEffect(() => {
    let appliedToCopy = [false, false, false, false, false, false, false];
    appliedToCopy[props.d] = true;
    setAppliedTo(appliedToCopy);
  }, [props.d]);


  React.useEffect(() => {
    setShiftsInfo(
      props.shifts_info?.map((shift) => normalizeShift(shift)) || []
    );
    setDeleteShifts([]);
  }, [props.shifts_info]);

  function handleAppliedToChange(day_i) {
    let appliedToCopy = [...appliedTo];
    if (day_i !== props.d) {
      appliedToCopy[day_i] = !appliedTo[day_i];
    }
    // console.log(appliedToCopy);
    setAppliedTo(appliedToCopy);
  }

  function appliedToButtonGroup() {
    return (
      <div className="w-full mt-2 bg-slate-50 rounded-lg flex">
        <ThemeProvider theme={theme}>
          <ButtonGroup
            style={{
              flex: 1,
              border: "1px solid #f1f5f9",
              borderRadius: "7px",
            }}
          >
            {appliedTo.map((d, i) => (
              <Button
                fullWidth
                color="neutral"
                size="small"
                style={{
                  border: d ? "1px solid #6d28d9" : "1px solid #f8fafc",
                  borderRadius: "7px",
                  backgroundColor: d ? "#ede9fe" : "transparent",
                  padding: "6px",
                  margin: "0 0.5px",
                }}
                key={i}
                onClick={() => handleAppliedToChange(i)}
              >
                {day_abbrevs[i]}
              </Button>
            ))}
          </ButtonGroup>
        </ThemeProvider>
      </div>
    );
  }


  function submitAndClose(shift_save_type) {
    if (shiftsInfo.length === 1 && deleteShifts.length == 0) {
      let state = shiftsInfo[0];
      let { _key, ...shift_info } = state;
      shift_info["hours"] =
        state.shift_end -
        state.shift_start -
        state.break_length -
        (state.break_length_2 || 0);
      shift_info["shift_length"] = state.shift_end - state.shift_start;
      shift_info["store_team_member"] = selectedPerson?.id || localSelectedEmployeeId;

      // console.log(shift_info);

      props.addShift(shift_info, shift_save_type, appliedTo);
      if (shift_save_type === "delete") {
        setDeletingLoading(true);
      } else {
        setSavingLoading(true);
      }
    } else {
      let shifts_arr = deleteShifts.map(({ _key, ...rest }) => rest);
      shiftsInfo.map((originalShift, index) => {
        let { _key, ...shift } = normalizeShift({ ...originalShift }); // copy to prevent mutation
        shift["hours"] =
          shift.shift_end -
          shift.shift_start -
          shift.break_length -
          (shift.break_length_2 || 0);
        shift["shift_length"] = shift.shift_end - shift.shift_start;
        shift["store_team_member"] = selectedPerson?.id || localSelectedEmployeeId;
        if (shift_save_type === "delete") {
          shift["action"] = "delete";
        } else {
          shift["action"] = shift["id"] > 0 ? "edit" : "add";
        }

        shifts_arr.push(shift);
      });
      props.addMultipleShifts(shifts_arr);
      if (shift_save_type === "delete") {
        setDeletingLoading(true);
      } else {
        setSavingLoading(true);
      }
    }
  }

  useEffect(() => {
    if (props.saveShiftFinished) {
      setSavingLoading(false);
      setDeletingLoading(false);
      props.handleClose();
    }
  }, [props.saveShiftFinished]);

  const day_abbrevs = createDayArray(props.businessWeekStartDayIndex);

  const checkShiftOverlaps = (shiftIndex) => {
    if (shiftsInfo.length <= 1) return { valid: true, message: "" };
    const current = shiftsInfo[shiftIndex];
    for (let i = 0; i < shiftsInfo.length; i++) {
      if (i === shiftIndex) continue;
      const other = shiftsInfo[i];
      if (current.shift_start < other.shift_end && current.shift_end > other.shift_start) {
        return { valid: false, message: "This shift overlaps with another shift." };
      }
    }
    return { valid: true, message: "" };
  };

  const validatePaidBreaks = (shift) =>
    validateShiftPaidBreaks(shift, Boolean(props.paidBreakInputsEnabled));

  const validateUnpaidBreaks = (shift) => {
    const unpaidBreaks = [];

    if ((shift.break_length || 0) > 0) {
      unpaidBreaks.push({
        label: "first",
        break_start: shift.break_start,
        break_length: shift.break_length,
      });
    }

    if ((shift.break_length_2 || 0) > 0) {
      if ((shift.break_length || 0) <= 0) {
        return {
          valid: false,
          message: "Add the first unpaid break before adding the second one.",
        };
      }
      unpaidBreaks.push({
        label: "second",
        break_start: shift.break_start_2,
        break_length: shift.break_length_2,
      });
    }

    for (let i = 0; i < unpaidBreaks.length; i++) {
      const breakEntry = unpaidBreaks[i];
      const breakStart = breakEntry.break_start;
      const breakEnd = breakStart + breakEntry.break_length;

      if (!Number.isFinite(breakStart)) {
        return {
          valid: false,
          message: `The ${breakEntry.label} unpaid break start must be set.`,
        };
      }

      if (breakStart <= shift.shift_start || breakEnd >= shift.shift_end) {
        return {
          valid: false,
          message: `The ${breakEntry.label} unpaid break must stay inside the shift window.`,
        };
      }
    }

    if (unpaidBreaks.length > 1) {
      const [firstBreak, secondBreak] = unpaidBreaks;
      const firstBreakEnd = firstBreak.break_start + firstBreak.break_length;

      if (secondBreak.break_start < firstBreakEnd) {
        return {
          valid: false,
          message: "The second unpaid break must start after the first one ends.",
        };
      }
    }

    return { valid: true, message: "" };
  };

  function updateShiftAtIndex(updatedShift, index) {
    setShiftsInfo((prevShifts) => {
      // Copy the array
      const updatedShifts = [...prevShifts];
      // Update the specific shift
      updatedShifts[index] = normalizeShift(updatedShift);
      return updatedShifts;
    });
  }

  function addShiftForm() {
    if (readOnly) {
      return;
    }

    //setNumShiftForms(numShiftForms + 1);
    // Default shift info
    let default_shift_info = { ...shiftsInfo[0] };
    delete default_shift_info._key;
    default_shift_info["shift_start"] = shiftsInfo[0]["shift_end"];
    default_shift_info["shift_end"] = shiftsInfo[0]["shift_end"] + 4;
    default_shift_info["break_start"] = shiftsInfo[0]["shift_end"];
    default_shift_info["break_end"] = shiftsInfo[0]["shift_end"];
    default_shift_info["break_length"] = 0;
    default_shift_info["break_start_2"] = 0;
    default_shift_info["break_end_2"] = 0;
    default_shift_info["break_length_2"] = 0;
    default_shift_info["id"] = 0;
    default_shift_info["paid_breaks"] = [];
    setShiftsInfo((prevShifts) => [
      ...prevShifts,
      normalizeShift(default_shift_info),
    ]);
  }

  function removeShiftFormAtIndex(index) {
    if (readOnly) {
      return;
    }

    let deleteInfo = shiftsInfo[index];
    if (deleteInfo["id"] > 0) {
      deleteInfo["action"] = "delete";
      setDeleteShifts((prevDeleteShifts) => [...prevDeleteShifts, deleteInfo]);
    }
    setShiftsInfo((prevShifts) => prevShifts.filter((_, i) => i !== index));
  }

  const checkIfAllValid = (requireAvailableRole = true) => {
    // If adding from monthly view, require employee selection
    if (!props.p && !selectedPerson) {
      return false;
    }
    // Require role selection for all shifts
    return shiftsInfo.every((shift, index) => {
      const originalShift = props.shifts_info?.find(
        (candidate) => candidate.id === shift.id
      );
      const keepsExistingRole =
        props.existingShift &&
        shift.id > 0 &&
        originalShift?.role_id === shift.role_id;

      return (
        shift.role_id &&
        (!requireAvailableRole ||
          roleIsAvailable(shift.role_id) ||
          keepsExistingRole) &&
        checkIfValid(shift, shift.break_length > 0) &&
        validateUnpaidBreaks(shift).valid &&
        validatePaidBreaks(shift).valid &&
        checkShiftOverlaps(index).valid
      );
    });
  };

  const canSubmitUnassignToOpenShift =
    canUnassignToOpenShift && checkIfAllValid(false);

  function submitUnassignToOpenShift() {
    if (!canSubmitUnassignToOpenShift) {
      return;
    }

    let state = shiftsInfo[0];
    let { _key, ...shift_info } = state;
    shift_info["hours"] =
      state.shift_end -
      state.shift_start -
      (state.break_length || 0) -
      (state.break_length_2 || 0);
    shift_info["shift_length"] = state.shift_end - state.shift_start;
    shift_info["store_team_member"] = null;
    shift_info["person"] = "";
    shift_info["status"] = "AVAILABLE";

    props.addShift(shift_info, "edit", appliedTo);
    setSavingLoading(true);
  }

  const handleOpenShiftActions = (event, shift = null) => {
    setAttendanceActionShiftId(shift?.id ?? null);
    setShiftActionsAnchorEl(event.currentTarget);
  };

  const handleCloseShiftActions = () => {
    setShiftActionsAnchorEl(null);
    setAttendanceActionShiftId(null);
  };

  const handleRequestUnassign = () => {
    handleCloseShiftActions();
    setUnassignConfirmOpen(true);
  };

  const handleCancelUnassign = () => {
    setUnassignConfirmOpen(false);
  };

  const handleConfirmUnassign = () => {
    setUnassignConfirmOpen(false);
    submitUnassignToOpenShift();
  };

  const handleSetMissedShiftReason = async (reason) => {
    const targetAttendanceInfo = attendanceActionInfo;
    const canApplyReason = reason === null
      ? targetAttendanceInfo.canClearAttendanceStatus
      : targetAttendanceInfo.canSetAttendanceStatus;

    if (
      !canApplyReason ||
      !targetAttendanceInfo.shift?.id ||
      attendanceStatusLoading
    ) {
      return;
    }

    handleCloseShiftActions();
    setAttendanceStatusLoading(true);
    try {
      const result = await props.post_missed_shift(
        targetAttendanceInfo.shift.id,
        reason
      );
      if (result) {
        await props.onMissedShiftUpdated?.(result);
        props.handleClose();
      }
    } finally {
      setAttendanceStatusLoading(false);
    }
  };

  const getAttendanceStatusCardClasses = (attendanceInfo) => {
    if (attendanceInfo.missedShiftReason) {
      return "border-rose-200 bg-rose-50";
    }
    if (attendanceInfo.needsAttendanceReview) {
      return "border-amber-200 bg-amber-50";
    }
    if (attendanceInfo.hasActualUsedSchedule) {
      return "border-emerald-200 bg-emerald-50";
    }
    return "border-slate-200 bg-white";
  };

  const renderAttendanceStatusCard = (attendanceInfo, className = "mt-3") => {
    if (!attendanceInfo.showAttendanceStatus) {
      return null;
    }

    return (
      <div
        className={`${className} rounded-lg border px-4 py-3 ${getAttendanceStatusCardClasses(
          attendanceInfo
        )}`}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium text-slate-900">
            Attendance status
          </p>
          {attendanceInfo.canManageAttendanceStatus && (
            <Tooltip title="Attendance status actions">
              <IconButton
                size="small"
                aria-label="Attendance status actions"
                onClick={(event) =>
                  handleOpenShiftActions(event, attendanceInfo.shift)
                }
                sx={{ marginTop: "-6px", marginRight: "-8px" }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-1">
          {attendanceInfo.attendanceStatusText}
        </p>
      </div>
    );
  };

  const inputsFooter = () => {
    return (
      <div className="flex gap-4 mt-4">
        {props.new_shift ? (
          <div />
        ) : (
          <LoadingButton
            variant="contained"
            loading={deletingLoading}
            loadingPosition="end"
            endIcon={<DeleteIcon />}
            onClick={() => submitAndClose("delete")}
            sx={{ textTransform: "none", borderRadius: "8px" }}
            color="error"
          >
            Delete
          </LoadingButton>
        )}
        <LoadingButton
          variant="contained"
          loading={savingLoading}
          loadingPosition="end"
          endIcon={<SaveIcon />}
          disabled={!checkIfAllValid()}
          onClick={() => submitAndClose(props.new_shift ? "add" : "edit")}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          {props.new_shift ? "Create" : "Save"}
        </LoadingButton>
      </div>
    );
  };

  const outerDivClass = {
    1: "",
    2: "w-full h-28 relative",
    3: "w-full h-40 relative",
  };

  const multiModalDisplay = (shift, shift_num, shift_total) => {
    const innerDivClass = {
      1: { 1: "mt-3" },
      2: { 1: "mt-3 absolute left-0 top-0", 2: "absolute left-28 top-12" },
      3: {
        1: "mt-3 absolute left-0 top-0",
        2: "absolute left-24 top-10",
        3: "absolute left-48 bottom-0",
      },
    };

    return (
      <div className={innerDivClass[shift_total][shift_num]}>
        <ShiftCardModalDisplay
          state={shift}
          availability_info={props.availability_info}
          role_names={props.role_names}
          role_types={props.role_types}
        />
      </div>
    );
  };

  const handleCloseNoSave = () => {
    setShiftsInfo(
      props.shifts_info?.map((shift) => normalizeShift(shift)) || []
    );
    props.handleClose();
  };

  return (
    <div>
      <Drawer anchor="right" open={props.open} onClose={handleCloseNoSave}>
        <div className="w-[450px] h-full bg-slate-50">
          <div className="max-h-screen overflow-auto">
            <div className="flex items-center justify-between px-6 py-8">
              <h2 className="text-slate-900 text-2xl">{drawerTitle}</h2>
              <div className="flex items-center space-x-1 text-slate-500">
                {showHeaderShiftActions && (
                  <Tooltip title="More actions">
                    <IconButton
                      size="small"
                      aria-label="More shift actions"
                      onClick={handleOpenShiftActions}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {showShiftActions && (
                  <Menu
                    anchorEl={shiftActionsAnchorEl}
                    open={shiftActionsOpen}
                    onClose={handleCloseShiftActions}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {canUnassignToOpenShift && (
                      <MenuItem
                        disabled={!canSubmitUnassignToOpenShift}
                        onClick={handleRequestUnassign}
                      >
                        Unassign to open shift
                      </MenuItem>
                    )}
                    {canUnassignToOpenShift &&
                      attendanceActionInfo.canManageAttendanceStatus && (
                      <Divider />
                    )}
                    {canSetAttendanceStatus &&
                      MISSED_SHIFT_REASON_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          disabled={attendanceStatusLoading || hasActualUsedSchedule}
                          onClick={() => handleSetMissedShiftReason(option.value)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    {canClearAttendanceStatus && (
                      <MenuItem
                        disabled={attendanceStatusLoading}
                        onClick={() => handleSetMissedShiftReason(null)}
                      >
                        Clear attendance status
                      </MenuItem>
                    )}
                  </Menu>
                )}
                <IconButton
                  size="small"
                  aria-label="Close"
                  onClick={handleCloseNoSave}
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
            <div className="w-full px-6 -mt-6 pb-24">
              <div>
                <p className="text-lg text-slate-500">{props.date_name}</p>
              </div>
              {showDrawerAttendanceStatus &&
                renderAttendanceStatusCard(primaryAttendanceInfo)}
              {/* Employee selector for monthly view */}
              {props.isMonthlyView && props.store_team_member_weekly_info && (
                <div className="mt-4 pb-5 border-b border-slate-200">
                  <p className="text-sm text-slate-500">Employee *</p>
                  <div className="mt-1">
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                      }}
                    >
                      <Select
                        value={localSelectedEmployeeId || ''}
                        onChange={handleEmployeeChange}
                        disabled={readOnly}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        }}
                      >
                        {props.store_team_member_weekly_info
                          .filter((employee) => {
                            // If no role is selected yet, show all employees
                            if (!shiftsInfo[0]?.role_id) {
                              return true;
                            }
                            // Filter to only employees who can work this role
                            return employee.available_role_info?.some(
                              (role) => role.scheduled_role === shiftsInfo[0].role_id
                            );
                          })
                          .map((employee) => (
                            <MenuItem key={employee.id} value={employee.id}>
                              {employee.first_name} {employee.last_name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              )}
              {selectedPerson && props.person_day_time_off_requests?.length > 0 ? (
                <div className="mt-2 pb-5 border-b border-slate-200 flex items-center">
                  <div className="w-3 h-3 rounded-full bg-rose-500 mr-3 shrink-0"></div>
                  <div className="w-full">
                    <p className="text-sm text-slate-500">
                      This employee has time off today {(props.person_day_time_off_requests?.[0]?.start_qhnum && props.person_day_time_off_requests?.[0]?.end_qhnum) && `from ${get_t_str(props.person_day_time_off_requests?.[0]?.start_qhnum)} to ${get_t_str(props.person_day_time_off_requests?.[0]?.end_qhnum)}`}
                    </p>
                  </div>
                </div>
              ) : (
                selectedPerson && availabilityInfo && (
                  <div className="py-3 border-b border-slate-200 flex items-center">
                    <div className={`w-3 h-3 rounded-full ${availabilityDotClass} mr-3 shrink-0`}></div>
                    <div className="w-full">
                      {availabilityInfo.available_from === 0 &&
                      availabilityInfo.available_to === 24 ? (
                        <p className="text-base text-emerald-700">
                          This employee is available all day
                        </p>
                      ) : availabilityInfo.available_to === 0 ? (
                        <p className="text-base text-slate-600">
                          This employee is unavailable today
                        </p>
                      ) : (
                        <div>
                          <p className="text-base text-slate-600">
                            This employee is available{" "}
                            {get_t_str(availabilityInfo.available_from)} to{" "}
                            {get_t_str(availabilityInfo.available_to)}
                          </p>
                          {availabilityInfo.available_to_2 && (
                            <p className="text-sm text-slate-500">
                              and{" "}
                              {get_t_str(
                                availabilityInfo.available_from_2
                              )}{" "}
                              to{" "}
                              {get_t_str(availabilityInfo.available_to_2)}
                            </p>
                          )}
                        </div>
                      )}
                      {availabilityInfo.preferred_to > 0 && (
                        <div className="text-sm text-slate-500 mt-1">
                          <p>
                            Preferred:{" "}
                            {get_t_str(availabilityInfo.preferred_from)} -{" "}
                            {get_t_str(availabilityInfo.preferred_to)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
              <div className="mt-5 pb-5 border-b border-slate-200">
                <div className="w-full flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-slate-900">
                    <h3 className="text-slate-900 font-bold text-sm">
                      Shift(s)
                    </h3>
                  </div>
                  {mostFrequentShifts?.length > 0 && selectedPerson && !props.isMonthlyView && !readOnly && (
                    <div className="flex items-center">
                      <Tooltip
                        title={`Quick fill ${
                          selectedPerson.first_name
                        }'s most commonly scheduled shift on ${convertToDayOfWeek(
                          props.date_name
                        )}. Or click the dropdown to see more information and options.`}
                        placement={`${
                          isFrequentShiftsDropdownOpen ? "top" : "bottom"
                        }`}
                      >
                        <div
                          className="w-40 h-8 bg-violet-700 hover:bg-violet-800 border-r border-violet-900 text-white flex items-center pl-4 space-x-2 text-sm rounded-l-md cursor-pointer"
                          onClick={() => fillShiftInputWithFrequentShift(0)}
                        >
                          <div>
                            <InputIcon
                              style={{ fontSize: "18px", marginBottom: "1px" }}
                            />
                          </div>
                          <p>
                            {get_t_str_shortened(
                              mostFrequentShifts?.[0]?.shift_start
                            )}{" "}
                            -{" "}
                            {get_t_str_shortened(
                              mostFrequentShifts?.[0]?.shift_end
                            )}
                          </p>
                        </div>
                      </Tooltip>
                      <div
                        className={`w-10 h-8 ${
                          isFrequentShiftsDropdownOpen
                            ? "bg-violet-800"
                            : "bg-violet-700"
                        } hover:bg-violet-800 text-white flex items-center justify-center rounded-r-md cursor-pointer relative`}
                        onClick={handleButtonClickFrequentShifts}
                        ref={dropdownRefFrequentShifts}
                      >
                        {isFrequentShiftsDropdownOpen ? (
                          <ExpandLessIcon style={{ fontSize: "22px" }} />
                        ) : (
                          <ExpandMoreIcon style={{ fontSize: "22px" }} />
                        )}
                        {isFrequentShiftsDropdownOpen && (
                          <div className="absolute w-[200px] top-full right-0 bg-white border border-slate-200 shadow-lg z-30 rounded-lg overflow-hidden">
                            {mostFrequentShifts?.map((shift, index) => {
                              return (
                                <div key={index}>
                                  <div
                                    className="px-4 py-3 hover:bg-slate-50 rounded-lg cursor-pointer relative"
                                    onClick={() =>
                                      fillShiftInputWithFrequentShift(index)
                                    }
                                  >
                                    <p className="text-slate-900 text-sm">
                                      {get_t_str(shift.shift_start)} -{" "}
                                      {get_t_str(shift.shift_end)}
                                    </p>
                                    <p className="text-slate-500 text-xs">
                                      {`B: ${formatShiftBreakSummary(shift)}`}
                                    </p>
                                    <p className="text-slate-500 text-xs">
                                      {getRoleName(
                                        shift.role_id_id,
                                        props.role_names
                                      )}
                                    </p>
                                  </div>
                                  {/* Conditionally render the divider if it's not the last index */}
                                  {index !== mostFrequentShifts.length - 1 && (
                                    <div>
                                      <Divider />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {shiftsInfo.map((shift, index) => (
                  <div
                    key={shift._key}
                    className="bg-white py-4 px-4 rounded-2xl mt-4 relative border border-slate-200 shadow-sm"
                  >
                    {shiftsInfo.length > 1 && !readOnly && (
                      <div className="absolute top-2 right-2">
                        <Tooltip title="Remove shift">
                          <IconButton size="small" onClick={() => removeShiftFormAtIndex(index)}>
                            <CloseIcon style={{ fontSize: "16px", color: "#94a3b8" }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                    <div className={readOnly ? "pointer-events-none" : ""}>
                    <ShiftSingleInputModal
                      p={selectedPerson}
                      shift_info={shift}
                      addShiftForm={addShiftForm}
                      numShiftForms={shiftsInfo.length}
                      onUpdateShift={(updatedShift) =>
                        updateShiftAtIndex(updatedShift, index)
                      }
                      role_names={props.role_names}
                      index={index}
                      start_qhnum={props.start_qhnum}
                      schedule_day_end_qhnum={props.schedule_day_end_qhnum}
                      breakWaiverEnabled={props.breakWaiverEnabled}
                      defaultBreakLengthHours={props.defaultBreakLengthHours}
                      breakRequiredWhenShiftLengthExceeds={
                        props.breakRequiredWhenShiftLengthExceeds
                      }
                      secondBreakShiftLengthThreshold={
                        props.secondBreakShiftLengthThreshold
                      }
                      open={props.open}
                      existingShift={props.existingShift}
                      readOnly={readOnly}
                      paidBreakInputsEnabled={props.paidBreakInputsEnabled}
                      businessDeclineShiftOptionEnabled={
                        props.businessDeclineShiftOptionEnabled
                      }
                      restBreakRegulation={props.restBreakRegulation}
                      validatePaidBreaks={validatePaidBreaks}
                      validateUnpaidBreaks={validateUnpaidBreaks}
                      overlapValidation={checkShiftOverlaps(index)}
                    />
                    </div>
                    {shiftsInfo.length > 1 &&
                      renderAttendanceStatusCard(
                        getAttendanceStatusInfo(shift),
                        "mt-4"
                      )}
                  </div>
                ))}
                {shiftsInfo.length < 3 && !readOnly && (
                  <div
                    className="w-full mt-4 py-3 border border-dashed border-violet-300 rounded-xl flex items-center justify-center cursor-pointer hover:bg-violet-50 hover:border-violet-400 transition-colors"
                    onClick={addShiftForm}
                  >
                    <AddIcon
                      style={{
                        fontSize: "16px",
                        color: "#64748B",
                        marginRight: "4px",
                      }}
                    />
                    <p className="text-sm text-slate-500">Add shift</p>
                  </div>
                )}
              </div>
              {props.new_shift && shiftsInfo.length === 1 && (
                <div className="mt-5 pb-5 border-slate-200">
                  <h3 className="text-sm text-slate-900 font-bold">Apply To</h3>
                  <div className="mt-2">{appliedToButtonGroup()}</div>
                </div>
              )}
            </div>
            <div className="w-full h-[40px]"></div>
            {readOnly ? (
              <DrawerFooterContainerSmall>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleCloseNoSave}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#475569",
                    "&:hover": {
                      backgroundColor: "#334155",
                    },
                    fontSize: "16px",
                  }}
                >
                  Close
                </Button>
              </DrawerFooterContainerSmall>
            ) : props.new_shift ? (
              <DrawerFooterContainerSmall>
                <LoadingButton
                  id="save-shift"
                  variant="contained"
                  loading={savingLoading}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  disabled={!checkIfAllValid()}
                  onClick={() => submitAndClose("add")}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    fontSize: "16px",
                  }}
                >
                  Create
                </LoadingButton>
              </DrawerFooterContainerSmall>
            ) : (
              <DrawerFooterContainerSmall>
                <div className="w-full h-full flex items-center">
                  <div className="w-1/2 pr-2">
                    <LoadingButton
                      fullWidth
                      variant="contained"
                      loading={deletingLoading}
                      loadingPosition="end"
                      endIcon={<DeleteIcon />}
                      onClick={() => submitAndClose("delete")}
                      sx={{
                        width: "100%",
                        textTransform: "none",
                        borderRadius: "12px",
                        paddingY: "14px",
                        backgroundColor: "#e11d48",
                        "&:hover": {
                          backgroundColor: "#be123c",
                        },
                        fontSize: "16px",
                      }}
                    >
                      Delete
                    </LoadingButton>
                  </div>
                  <div className="w-1/2 pl-2">
                    <LoadingButton
                      id="save-shift"
                      fullWidth
                      variant="contained"
                      loading={savingLoading}
                      loadingPosition="end"
                      endIcon={<SaveIcon />}
                      disabled={!checkIfAllValid()}
                      onClick={() => submitAndClose("edit")}
                      sx={{
                        width: "100%",
                        textTransform: "none",
                        borderRadius: "12px",
                        paddingY: "14px",
                        backgroundColor: "#6d28d9",
                        "&:hover": {
                          backgroundColor: "#5b21b6",
                        },
                        fontSize: "16px",
                      }}
                    >
                      Save
                    </LoadingButton>
                  </div>
                </div>
              </DrawerFooterContainerSmall>
            )}
          </div>
        </div>
      </Drawer>
      <Modal
        open={unassignConfirmOpen}
        onClose={handleCancelUnassign}
        aria-labelledby="unassign-open-shift-title"
        aria-describedby="unassign-open-shift-description"
      >
        <Box sx={style}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={handleCancelUnassign}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div>
            <h3
              id="unassign-open-shift-title"
              className="text-xl font-semibold text-gray-900"
            >
              Unassign shift?
            </h3>
            <p
              id="unassign-open-shift-description"
              className="mt-3 text-sm text-slate-600"
            >
              This removes {selectedPerson?.first_name}{" "}
              {selectedPerson?.last_name} from the shift and keeps the same
              time, role, station, and breaks as an open shift.
            </p>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button
                onClick={handleCancelUnassign}
                sx={{
                  textTransform: "none",
                  color: "#475569",
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                loading={savingLoading}
                loadingPosition="end"
                disabled={!canSubmitUnassignToOpenShift}
                onClick={handleConfirmUnassign}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  backgroundColor: "#6d28d9",
                  "&:hover": {
                    backgroundColor: "#5b21b6",
                  },
                }}
              >
                Make open shift
              </LoadingButton>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default ShiftInputsModal;
